import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, OnDestroy, Output, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { F8824Service } from './f-8824.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import { safeEvalFormula, isFiniteEvalNumber } from '../../../shared/_utils/safe-eval-formula';

import {
  buildHeaderDataShell,
  extractObject,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-f-8824',
    templateUrl: './f-8824.component.html',
    styleUrls: ['./f-8824.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class F8824Component implements OnInit, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // STANDARD COMPONENT PROPERTIES
  minimise = false;
  isSaveClicked = false;
  canSave = true;
  isLocked = false;
  private lockObserver?: MutationObserver;
  activeTab = 1; // Single tab, used for element IDs only

  // Modal properties
  modalTitle = "Form 8824";
  
  // Entity data properties (custom structure from original)
  entityInfo: any = {};

  // Loading states
  loadingData = true;
  crudLoading = false;

  // Data from AngularJS
  rowData: any = {};
  filterParams: any = {};
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  appService: any = { USER_SETTINGS: {}, SERVER_CONFIG: {} };

  private readonly subs = new Subscription();
  
  // Component-specific data
  f8824Header: any = {};
  f8824List1: any[] = [];
  tableHeader: any[] = [];
  f8824MasterData: any[] = [];
  f8824PropertyTable: any[] = [];
  f8824OccData: any[] = [];
  f8824OccDataOriginal: any[] = [];
  f8824TransRowData: any[] = [];
  f8824TransList: any[] = [];
  f8824TransList1: any[] = [];
  f8824BodyTransList: any[] = [];
  f8824OriginalData: any[] = [];
  f8824OriginalRadioData: any[] = [];
  f6252FinalData: any[] = [];
  
  // Occurrence management
  maxOcc: number = 0;
  maxOccObj: any;
  currentOccurrence: number = 0;
  
  // Display flags
  showOccurrenceTable = false;
  showTransAttributes = false;
  initialLoad = false;
  
  // Lists
  outerList: any[] = [];
  innerList: any[] = [];
  radioLines: any[] = [];
  
  // LOV data
  formLOV: any[] = [];
  formCalcData: any[] = [];
  
  // User settings
  logged_in_user: string = '';
  curentyear: string = '';
  
  // Calendar
  calendar: any = {
    opened: false
  };
  
  // Calculation flag
  currentOccValChanged = false;
  
  // Date options
  dateOptions: any = {
    formatYear: 'yyyy',
    startingDay: 1
  };

  constructor(
    private f8824Service: F8824Service,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.init();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.init();
        this.cdr.markForCheck();
      })
    );
  }

  ngAfterViewInit(): void {
    this.detectLockState();
    setTimeout(() => this.detectLockState(), 60);
    setTimeout(() => this.detectLockState(), 300);
    this.lockObserver = new MutationObserver(() => this.detectLockState());
    this.lockObserver.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class'] });
  }

  private detectLockState(): void {
    let el: HTMLElement | null = this.elementRef.nativeElement;
    while (el) {
      if (el.classList?.contains('modal-window-lock-class')) {
        if (!this.isLocked) {
          this.isLocked = true;
          this.cdr.markForCheck();
        }
        return;
      }
      el = el.parentElement;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.lockObserver?.disconnect();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow) ? extractObject(rawRow[0]) : extractObject(rawRow);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.USER_SETTINGS = this.appService.USER_SETTINGS;
    this.SERVER_CONFIG = this.appService.SERVER_CONFIG;
    this.cdr.markForCheck();
  }

  private isInitialized = false;

  init() {
    try {
      // Check if all required data is available before initializing
      if (!this.globalService || Object.keys(this.globalService).length === 0 ||
          !this.GENERAL_CONFIG || !this.GENERAL_CONFIG.base_url ||
          !this.rowData || Object.keys(this.rowData).length === 0) {
        return;
      }
      
      // Check if already initialized to prevent multiple calls
      if (this.isInitialized) {
        return;
      }
      
      this.isInitialized = true;
      this.setupEntityData();
      this.curentyear = this.filterParams.tax_year || this.globalService.globalParams?.tax_year;
      this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
      
      // Load LOV and data
      this.fetchLOV();
      this.fetchModalCalcData();
    } catch (e) {
      console.error('Error in F8824 init:', e);
    }
  }

  private setupEntityData() {
    // Custom entity structure from original component
    this.entityInfo = {
      leid: this.rowData.LEID,
      leName: this.rowData.LE_NAME,
      taxYearBegin: this.rowData.TAX_YEAR_BEGIN,
      taxYearEnd: this.rowData.TAX_YEAR_END,
      meCode: this.rowData.ME_CODE,
      meName: this.rowData.ME_NAME,
      cdrNo: this.rowData.CDR_NO
    };
  }

  fetchLOV() {
    const params = {
      action_code: 'sjh60s',
      sso_id: this.logged_in_user,
      tax_year: this.curentyear,
      scenario: this.globalService.globalParams?.scenario,
      jcd_key: this.globalService.globalParams?.jcd_key,
      form_no: '6252'
    };
    
    this.f8824Service.fetchLOV(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.formLOV = data.jsonObject;
          this.getF8824TransData();
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching LOV:', error);
        this.gtwSnackbarService.openSnackBar('Error loading form data', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  fetchModalCalcData() {
    const params = {
      action_code: '1ubsuu',
      tax_year: this.curentyear,
      scenario: this.globalService.globalParams?.scenario,
      jcd_key: this.globalService.globalParams?.jcd_key,
      form_no: '6252',
      schedule_name: 'N/A',
      part_no: '',
      section_name: 'N/A'
    };
    
    this.f8824Service.fetchModalCalcData(this.GENERAL_CONFIG.base_url, params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject) {
          this.formCalcData = data.jsonObject;
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching calc data:', error);
        this.cdr.markForCheck();
      }
    );
  }

  getF8824TransData() {
    this.f8824Service.fetchF8824Details(
      this.rowData, 
      this.logged_in_user, 
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.f8824TransList = data && data.jsonObject ? data.jsonObject : [];

        // Header Transactions
        this.f8824TransList1 = this.f8824TransList.filter((obj: any) => obj.ROW_TYPE == 'CH');

        // Body Transactions
        this.f8824BodyTransList = this.f8824TransList.filter((obj: any) => obj.ROW_TYPE !== 'CH');

        // Group by OCCURRENCE
        const grouped = _.groupBy(this.f8824TransList1, 'OCCURRENCE');
        
        this.f8824TransRowData = Object.keys(grouped).map(key => {
          return {
            ROW_TYPE: grouped[key][0].ROW_TYPE,
            Row_data: grouped[key],
            OCCURRENCE: grouped[key][0].OCCURRENCE,
            IS_EDITTED: "N",
            DELETE_ROWS: []
          };
        });
        
        
        this.maxOccObj = _.maxBy(this.f8824TransRowData, 'OCCURRENCE');
        if (this.maxOccObj != undefined) {
          this.maxOcc = this.maxOccObj.OCCURRENCE;
        } else {
          this.maxOcc = 0;
        }

        this.getF8824MasterData();
        this.cdr.markForCheck();
      },
      (error: any) => {
        console.error('Error fetching F8824 details:', error);
        this.gtwSnackbarService.openSnackBar('Error loading form details', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  getF8824MasterData() {
    this.f8824Service.fetchF8824MasterData(
      this.rowData, 
      this.logged_in_user, 
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        this.f8824Header = data.jsonObject && data.jsonObject[0] ? data.jsonObject[0] : {};
        this.modalTitle = this.f8824Header?.ATTRIB_DESCRIPTION || "Form 8824";
        this.f8824List1 = data.jsonObject ? data.jsonObject.slice(1) : [];
        this.tableHeader = this.f8824List1.filter((obj: any) => obj.ROW_TYPE === "TH");
        
        this.f8824MasterData = this.f8824List1.filter((obj: any) => obj.ROW_TYPE !== 'TH');

        this.f8824PropertyTable = this.f8824List1.filter((obj: any) => obj.ROW_TYPE == 'CH');
        
        // Group by ROW_TYPE
        const grouped = _.groupBy(this.f8824PropertyTable, 'ROW_TYPE');
        
        this.f8824OccData = Object.keys(grouped).map(key => {
          return {
            ROW_TYPE: grouped[key][0].ROW_TYPE,
            Row_data: grouped[key],
            OCCURRENCE: null,
            IS_EDITTED: "N",
            DELETE_ROWS: []
          };
        });
        
        this.f8824OccDataOriginal = _.cloneDeep(this.f8824OccData);

        this.outerList = [];
        this.innerList = [];
        this.radioLines = [];
        
        for (let i = 0; i < this.f8824MasterData.length; i++) {
          if (this.f8824MasterData[i].FORM_LINE_KEY != null) {
            // radio
            if (this.f8824MasterData[i].EDIT_TYPE == "radio") {
              this.radioLines.push(this.f8824MasterData[i]);
            }
            
            let tempCheck1: any[] = [];
            if (this.outerList != null && this.outerList.length > 0) {
              tempCheck1 = this.outerList.filter((obj: any) => 
                obj.FORM_LINE_KEY === this.f8824MasterData[i].FORM_LINE_KEY && 
                obj.IS_FORM_DISPLAY === 'Y'
              );
            }
            
            if (tempCheck1.length == 0) {
              this.outerList.push(this.f8824MasterData[i]);
            }
          }
        }

        if (this.f8824TransRowData.length != 0) {
          this.showOccurrenceTable = true;
          this.f8824OccDataOriginal = this.f8824TransRowData;
        }

        this.loadingData = false;
        this.recomputeCanSave();
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.gtwSnackbarService.openSnackBar('Error loading form master data', 'gtw-snackbar--red');
        this.loadingData = false;
        this.cdr.markForCheck();
      }
    );
  }

  checkBoxHandler(lineKey: any, attribKey: any, attribCol: any, occurrence: any) {
    const checkedVal = this.radioLines.filter((obj: any) => 
      obj.FORM_LINE_KEY === lineKey && 
      obj.ATTRIB_COL === attribCol && 
      obj.OCCURRENCE === occurrence && 
      obj.IS_FORM_DISPLAY === 'Y'
    );

    _.forEach(checkedVal, (el: any, index: number) => {
      if (el.LINE_ATTRIB_KEY == attribKey) {
        el.ATTRIB_VALUE = 'true';
        el.IS_CHANGED_FLAG = 'Y';
      } else {
        el.ATTRIB_VALUE = 'false';
        el.IS_CHANGED_FLAG = 'Y';
      }
    });
  }

  reset() {
    this.getF8824TransData();
    this.showTransAttributes = false;
  }

  save(occurrence: number, type: string) {
    let finalData: any[] = [];
    let clob_settings: any[] = [];

    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar("Please wait while we save this request", 'gtw-snackbar--yellow');
      return;
    }

    if (this.isLocked) {
      this.gtwSnackbarService.openSnackBar('This entity is locked. Save is disabled.', 'gtw-snackbar--red');
      return;
    }

    this.f6252FinalData = _.cloneDeep(this.f8824MasterData);

    if (type != 'delete' && _.isEqual(this.f8824MasterData, this.f8824OriginalData)) {
      this.cancel();
    } else {
      const returnValue = this.validateFormValues(this.currentOccurrence);
      if (returnValue == 1) {
        this.gtwSnackbarService.openSnackBar(
          'Please select Long/Short Term and Schedule before saving.',
          'gtw-snackbar--red'
        );
        this.cdr.markForCheck();
        return;
      }
      
      const finalLines = _.filter(this.f6252FinalData, (o: any) => o.OCCURRENCE == occurrence);
      const finalOccLines = _.filter(this.f8824OccDataOriginal, (o: any) => o.OCCURRENCE == occurrence);
      
      if (type == 'save') {
        for (let k = 0; k < finalLines.length; k++) {
          if (finalLines[k].ROW_TYPE != 'CH') {
            // For dates
            if (finalLines[k].EDIT_TYPE == 'custom_date_various' && finalLines[k].ATTRIB_VALUE) {
              finalLines[k].ATTRIB_VALUE = this.formatDateForSave(finalLines[k].ATTRIB_VALUE);
            }
            finalData.push(finalLines[k]);
          }
        }
        
        for (let m = 0; m < finalOccLines.length; m++) {
          if (finalOccLines[m].ROW_TYPE === 'CH') {
            for (let n = 0; n < finalOccLines[m].Row_data.length; n++) {
              finalOccLines[m].Row_data[n].IS_CHANGED_FLAG = "Y";
              finalData.push(finalOccLines[m].Row_data[n]);
            }
          }
        }
      } else {
        // If deleted row
        for (let k = 0; k < finalLines.length; k++) {
          if (finalLines[k].ROW_TYPE != 'CH') {
            finalLines[k].IS_CHANGED_FLAG = "D";
            finalData.push(finalLines[k]);
          }
        }

        if (finalOccLines[0].DELETE_ROWS.length != null && finalOccLines[0].DELETE_ROWS.length != undefined) {
          if (finalOccLines[0].ROW_TYPE === 'CH') {
            for (let m = 0; m < finalOccLines[0].DELETE_ROWS.length; m++) {
              finalOccLines[0].DELETE_ROWS[m].IS_CHANGED_FLAG = "D";
              finalData.push(finalOccLines[0].DELETE_ROWS[m]);
            }
          }
        }
      }
      
      clob_settings = [{
        sso_id: this.logged_in_user,
        screen_key: this.globalService.globalParams?.screen_key
      }];
      
      this.sendDetails(finalData, clob_settings);
    }
  }

  sendDetails(final_data: any[], clob_settings: any[]) {
    const message = "Form 8824 Details are successfully updated.";
    this.crudLoading = true;
    this.isSaveClicked = true;
    
    this.f8824Service.saveForm8824(
      this.GENERAL_CONFIG.base_url,
      final_data,
      clob_settings,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        
        if (response.errorMessage != 'null' && response.errorMessage) {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.markForCheck();
      },
      (error: any) => {
        this.crudLoading = false;
        this.isSaveClicked = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.markForCheck();
      }
    );
  }

  formatDateForSave(date: any): string {
    if (!date || date === 'VARIOUS') return date;
    
    // If already in MM/DD/YYYY format, return as is
    if (typeof date === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(date)) {
      return date;
    }
    
    const d = new Date(date);
    const month = ('0' + (d.getMonth() + 1)).slice(-2);
    const day = ('0' + d.getDate()).slice(-2);
    const year = d.getFullYear();
    
    return `${month}/${day}/${year}`;
  }

  private hasUnsetRequiredFields(occurrence: number): boolean {
    const occData = _.filter(this.f8824OccDataOriginal, (o: any) => o.OCCURRENCE == occurrence);
    for (const item of occData) {
      if (item.ROW_TYPE !== 'CH') continue;
      for (const row of item.Row_data) {
        if (row.OCCURRENCE != occurrence) continue;
        const ls = item.Row_data.find((x: any) => x.ATTRIB_SHORT_CODE === 'LONG_SHORT_TERM_' + row.LINE_NO);
        const sc = item.Row_data.find((x: any) => x.ATTRIB_SHORT_CODE === 'SCHEDULE_' + row.LINE_NO);
        if (ls && ls.ATTRIB_VALUE == null) return true;
        if (sc && sc.ATTRIB_VALUE == null) return true;
      }
    }
    return false;
  }

  private recomputeCanSave(): void {
    this.canSave = !this.hasUnsetRequiredFields(this.currentOccurrence);
    this.cdr.markForCheck();
  }

  validateFormValues(currentOccurrence: number): number {
    let returnValue = 0;
    
    const currentOccData = _.filter(this.f8824OccDataOriginal, (o: any) => o.OCCURRENCE == currentOccurrence);

    for (let i = 0; i < currentOccData.length; i++) {
      if (currentOccData[i].ROW_TYPE == 'CH') {
        for (let j = 0; j < currentOccData[i].Row_data.length; j++) {
          const line_no = currentOccData[i].Row_data[j].LINE_NO;

          if (currentOccData[i].Row_data[j].OCCURRENCE == currentOccurrence) {
            const long_short_index = currentOccData[i].Row_data.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "LONG_SHORT_TERM_" + line_no
            );
            const schedule_index = currentOccData[i].Row_data.findIndex((x: any) => 
              x.ATTRIB_SHORT_CODE == "SCHEDULE_" + line_no
            );

            if (long_short_index >= 0 && currentOccData[i].Row_data[long_short_index].ATTRIB_VALUE == null) {
              currentOccData[i].Row_data[long_short_index].HAS_ERROR = "Y";
              currentOccData[i].Row_data[long_short_index].ATTRIB_VALUE_ERROR = "Please select Long/Short Term.";
              returnValue = 1;
            }
            
            if (schedule_index >= 0 && currentOccData[i].Row_data[schedule_index].ATTRIB_VALUE == null) {
              currentOccData[i].Row_data[schedule_index].HAS_ERROR = "Y";
              currentOccData[i].Row_data[schedule_index].ATTRIB_VALUE_ERROR = "Please select Schedule.";
              returnValue = 1;
            }
          }
        }
      }
    }
    
    return returnValue;
  }

  AddNewRow(occurrence: any, line: any, index: any) {
    
    // On Add check if any Property details are editted
    const prevEdittedRow = this.f8824OccDataOriginal.filter((obj: any) => obj.IS_PROP_CHANGED == 'Y');

    if (prevEdittedRow.length > 0) {
      this.gtwSnackbarService.openSnackBar("Please save the details of the changed S No " + prevEdittedRow[0].OCCURRENCE + "  before you Add a new S No", 'gtw-snackbar--red');
      return;
    }

    // On Add check if already any New Property is added
    const newRow = this.f8824OccDataOriginal.filter((obj: any) => obj.IS_EDITTED == 'Y');

    if (newRow.length > 0) {
      this.gtwSnackbarService.openSnackBar("Please save the details of the S No " + newRow[0].OCCURRENCE + "  before you Add a new S No", 'gtw-snackbar--red');
      return;
    }

    if (this.maxOcc == 0) {
      this.maxOcc = this.maxOcc + 1;
      
      // Check if f8824OccDataOriginal has any elements
      if (this.f8824OccDataOriginal.length > 0) {
        this.f8824OccDataOriginal[0].OCCURRENCE = this.maxOcc;
        this.f8824OccDataOriginal[0].IS_EDITTED = "Y";
        
        _.forEach(this.f8824OccDataOriginal[0].Row_data, (dl: any, index: number) => {
          if (dl.ATTRIB_SHORT_CODE == 'SNO_N/A') {
            dl.ATTRIB_VALUE = this.maxOcc;
          } else {
            dl.ATTRIB_VALUE = null;
          }
          dl.OCCURRENCE = this.maxOcc;
        });
      } else {
        // Create new row structure if f8824OccDataOriginal is empty
        const row_data_copy = _.cloneDeep(this.f8824PropertyTable);
        _.forEach(row_data_copy, (item: any) => {
          if (item.ATTRIB_SHORT_CODE == 'SNO_N/A') {
            item.ATTRIB_VALUE = this.maxOcc;
          } else {
            item.ATTRIB_VALUE = null;
          }
          item.OCCURRENCE = this.maxOcc;
          item.IS_CHANGED_FLAG = "Y";
        });
        
        const new_row = {
          ROW_TYPE: 'CH',
          Row_data: row_data_copy,
          OCCURRENCE: this.maxOcc,
          IS_EDITTED: "Y",
          IS_NEW_ROW: "Y",
          DELETE_ROWS: []
        };
        this.f8824OccDataOriginal.push(new_row);
      }
    } else {
      this.maxOcc = this.maxOcc + 1;
      
      // Make sure we have at least one row to clone from
      if (this.f8824OccDataOriginal.length > 0) {
        const new_row = _.cloneDeep(this.f8824OccDataOriginal[0]);
        new_row.OCCURRENCE = this.maxOcc;
        new_row.IS_EDITTED = "Y";
        new_row.IS_NEW_ROW = "Y";

        for (let j = 0; j < new_row.Row_data.length; j++) {
          if (new_row.Row_data[j].EDIT_TYPE == "radio" && new_row.Row_data[j].DATA_TYPE == "string") {
            new_row.Row_data[j].ATTRIB_VALUE = null;
          } else if (new_row.Row_data[j].EDIT_TYPE == "label" && new_row.Row_data[j].DATA_TYPE == "string") {
            new_row.Row_data[j].ATTRIB_VALUE = this.maxOcc;
          } else {
            new_row.Row_data[j].ATTRIB_VALUE = null;
          }
          new_row.Row_data[j].OCCURRENCE = this.maxOcc;
          new_row.Row_data[j].IS_CHANGED_FLAG = "Y";
        }
        this.f8824OccDataOriginal.push(new_row);
      } else {
        // Fallback: create new row structure if f8824OccDataOriginal is empty
        const row_data_copy = _.cloneDeep(this.f8824PropertyTable);
        _.forEach(row_data_copy, (item: any) => {
          if (item.EDIT_TYPE == "radio" && item.DATA_TYPE == "string") {
            item.ATTRIB_VALUE = null;
          } else if (item.EDIT_TYPE == "label" && item.DATA_TYPE == "string") {
            item.ATTRIB_VALUE = this.maxOcc;
          } else {
            item.ATTRIB_VALUE = null;
          }
          item.OCCURRENCE = this.maxOcc;
          item.IS_CHANGED_FLAG = "Y";
        });
        
        const new_row = {
          ROW_TYPE: 'CH',
          Row_data: row_data_copy,
          OCCURRENCE: this.maxOcc,
          IS_EDITTED: "Y",
          IS_NEW_ROW: "Y",
          DELETE_ROWS: []
        };
        this.f8824OccDataOriginal.push(new_row);
      }
    }

    for (let m = 0; m < this.radioLines.length; m++) {
      this.radioLines[m]['OCCURRENCE'] = this.maxOcc;
    }

    for (let i = 0; i < this.outerList.length; i++) {
      if (this.outerList[i].EDIT_TYPE == "custom_date_various" && this.outerList[i].DATA_TYPE == "dateVarious") {
        this.outerList[i].ATTRIB_VALUE = null;
        this.outerList[i]['OCCURRENCE'] = this.maxOcc;
      } else if (this.outerList[i].EDIT_TYPE == "currency" && this.outerList[i].DATA_TYPE == "number") {
        this.outerList[i].ATTRIB_VALUE = 0;
        this.outerList[i]['OCCURRENCE'] = this.maxOcc;
      } else if (this.outerList[i].EDIT_TYPE == "label" && this.outerList[i].DATA_TYPE == "string") {
        this.outerList[i]['OCCURRENCE'] = this.maxOcc;
        if (this.outerList[i].ATTRIB_SHORT_CODE == 'SNO_N/A') {
          this.outerList[i].ATTRIB_VALUE = this.maxOcc;
        } else {
          this.outerList[i].ATTRIB_VALUE = null;
        }
      } else {
        this.outerList[i].ATTRIB_VALUE = null;
        this.outerList[i]['OCCURRENCE'] = this.maxOcc;
      }
      this.currentOccurrence = this.maxOcc;
    }

    // Original Data
    this.f8824OriginalData = _.cloneDeep(this.f8824MasterData);
    this.f8824OriginalRadioData = _.cloneDeep(this.radioLines);

    this.showOccurrenceTable = true;
    this.showTransAttributes = true;
    
    this.cdr.detectChanges();
  }

  editExistingRecord(line_no: any, occurrence: any, index: any, type: any) {
    // On edit show the check if any Property details are editted
    const prevEdittedRow = this.f8824OccDataOriginal.filter((obj: any) => 
      obj.IS_PROP_CHANGED == 'Y' && obj.OCCURRENCE != occurrence
    );

    if (prevEdittedRow.length > 0) {
      this.gtwSnackbarService.openSnackBar("Please save the details of the changed S No " + prevEdittedRow[0].OCCURRENCE + "  before you Edit a new S No", 'gtw-snackbar--red');
      return;
    }

    // On edit check if already any New Property is added
    const excistRow = this.f8824OccDataOriginal.filter((obj: any) => obj.IS_EDITTED == 'Y');

    if (excistRow.length > 0) {
      this.gtwSnackbarService.openSnackBar("Please save the details of the S No " + excistRow[0].OCCURRENCE + "  before you edit other S No", 'gtw-snackbar--red');
      return;
    }
    
    // On edit show the save circle symbol
    const edittedRow = this.f8824OccDataOriginal.filter((obj: any) => obj.OCCURRENCE == occurrence);
    edittedRow[0].IS_EDITTED = 'Y';

    // To show the ediited values of same occurrence
    if (edittedRow[0].IS_PROP_CHANGED != 'Y') {
      this.assignTransToMasterData(occurrence);
    }
  
    this.currentOccurrence = occurrence;
    this.showOccurrenceTable = true;
    this.showTransAttributes = true;
    this.cdr.detectChanges();
  }

  getFilteredOuterList(): any[] {
    // Filter outerList exactly like AngularJS filter
    return this.outerList.filter((row: any) => 
      row.IS_FORM_DISPLAY === 'Y' && 
      row.OCCURRENCE === this.currentOccurrence && 
      row.ROW_TYPE !== 'CH'
    );
  }

  assignTransToMasterData(occurrence: number) {
    // Body Occ Transactions
    const f8824BodyOccTransList = this.f8824BodyTransList.filter((obj: any) => obj.OCCURRENCE == occurrence);

    for (let j = 0; j < this.outerList.length; j++) {
      this.outerList[j]['OCCURRENCE'] = occurrence;

      for (let i = 0; i < f8824BodyOccTransList.length; i++) {
        const currentList = this.outerList.filter((obj: any) => 
          (obj.LINE_ATTRIB_KEY == f8824BodyOccTransList[i].LINE_ATTRIB_KEY) && 
          f8824BodyOccTransList[i].EDIT_TYPE != 'radio'
        );
        
        if (currentList.length != 0) {
          // Keep date as string (don't convert to Date object)
          // convertToDateInputFormat() expects MM/DD/YYYY string
          currentList[0].ATTRIB_VALUE = f8824BodyOccTransList[i].ATTRIB_VALUE;
          currentList[0].TRANS_DETAILS_KEY = f8824BodyOccTransList[i].TRANS_DETAILS_KEY;
        }
      }
    }

    for (let m = 0; m < this.radioLines.length; m++) {
      this.radioLines[m]['OCCURRENCE'] = occurrence;

      for (let i = 0; i < f8824BodyOccTransList.length; i++) {
        const currentList = this.radioLines.filter((obj: any) => 
          (obj.LINE_ATTRIB_KEY == f8824BodyOccTransList[i].LINE_ATTRIB_KEY) && 
          f8824BodyOccTransList[i].EDIT_TYPE == 'radio'
        );
        
        if (currentList.length != 0) {
          currentList[0].ATTRIB_VALUE = f8824BodyOccTransList[i].ATTRIB_VALUE;
        }
      }
    }

    // Original Data
    this.f8824OriginalData = _.cloneDeep(this.f8824MasterData);
    this.f8824OriginalRadioData = _.cloneDeep(this.radioLines);
  }

  removeExistingRecord(line_no: any, Occurrence: any, index: any) {
    // On delete check if already any Property is editted
    const editRow = this.f8824OccDataOriginal.filter((obj: any) => 
      obj.IS_PROP_CHANGED == 'Y' && obj.OCCURRENCE != Occurrence
    );

    if (editRow.length > 0) {
      this.gtwSnackbarService.openSnackBar("Please save the details of the S No " + editRow[0].OCCURRENCE + "  before you delete a S No " + Occurrence + ".", 'gtw-snackbar--red');
      return;
    }

    // Show browser confirmation dialog
    const confirmed = window.confirm('Are you sure you want to permanently delete this property?');
    
    if (!confirmed) {
      return;
    }

    let deleteSave = false;
    
    for (let i = 0; i < this.f8824OccDataOriginal.length; i++) {
      for (let j = 0; j < this.f8824OccDataOriginal[i].Row_data.length; j++) {
        if (this.f8824OccDataOriginal[i].IS_NEW_ROW == "Y" && 
            this.f8824OccDataOriginal[i].OCCURRENCE != "" && 
            null != this.f8824OccDataOriginal[i].OCCURRENCE && 
            this.f8824OccDataOriginal[i].OCCURRENCE == Occurrence) {
          // never saved in to DB
          this.f8824OccDataOriginal[i].DELETE_ROWS = [];
          deleteSave = false;
        } else {
          if (this.f8824OccDataOriginal[i].OCCURRENCE == Occurrence) {
            this.f8824OccDataOriginal[i].DELETE_ROWS.push(this.f8824OccDataOriginal[i].Row_data[j]);
            deleteSave = true;
          }
        }
      }
    }

    this.currentOccurrence = Occurrence;
    if (deleteSave == true) {
      this.assignTransToMasterData(this.currentOccurrence);
      this.save(this.currentOccurrence, 'delete');
    } else {
      this.reset();
    }
  }

  saveNewEdittedRecord(line_no: any, occurrence: any, index: any) {
    for (let i = 0; i < this.f8824OccDataOriginal.length; i++) {
      if (this.f8824OccDataOriginal[i].OCCURRENCE == occurrence) {
        this.f8824OccDataOriginal[i].IS_EDITTED = 'N';
        
        // To fill the required fields
        const returnValue = this.validateFormValues(occurrence);
        if (returnValue == 1) {
          this.f8824OccDataOriginal[i].IS_EDITTED = 'Y';
          return;
        }
      }
    }
    this.showTransAttributes = false;
    this.cdr.detectChanges();
  }

  valuechanged(row: any, occurrence: any, line_no: any, data_type: any) {
    const outerListLine = _.filter(this.outerList, (o: any) => 
      o.LINE_NO == line_no && o.OCCURRENCE == occurrence
    );
    
    if (outerListLine.length > 0) {
      outerListLine[0].IS_CHANGED_FLAG = "Y";
      outerListLine[0].HAS_ERROR = "N";
    }

    if (data_type != 'number') {
      const OccLines = _.filter(this.f8824OccDataOriginal, (o: any) => o.OCCURRENCE == occurrence);
      if (OccLines.length > 0) {
        OccLines[0].IS_PROP_CHANGED = 'Y';
      }
      row.HAS_ERROR = "N";
    }
    
  }

  updateAmount(index: any, occurence: any, line_no: any) {
    this.calcFinalAmounts(occurence);
  }

  calcFinalAmounts(occurence: number) {
    for (let i = 0; i < this.f8824MasterData.length; i++) {
      if (this.f8824MasterData[i].ROW_TYPE != 'CH') {
        if (this.f8824MasterData[i].ATTRIB_CALC_FORMULA != null) {
          this.f8824MasterData[i].ATTRIB_VALUE = this.calc_final_amt(
            this.f8824MasterData[i].ATTRIB_CALC_FORMULA, 
            occurence
          );
          
          if (this.f8824MasterData[i].IS_FORM_DISPLAY == 'Y') {
            this.f8824MasterData[i].IS_CHANGED_FLAG = "Y";
            
            // To Know if Attributes of this Occ is changed
            if (this.f8824MasterData[i].DATA_TYPE == 'number' && this.currentOccValChanged == true) {
              const OccNumLines = _.filter(this.f8824OccDataOriginal, (o: any) => o.OCCURRENCE == occurence);
              if (OccNumLines.length > 0) {
                OccNumLines[0].IS_PROP_CHANGED = 'Y';
              }
            }
          }
        }
      }
    }
  }

  calc_final_amt(formula: string, occurence: number): number {
    const matches = formula.match(/\b[^\d\s]+\b/g);
    let lineFormulaArray: string[] = [];
    
    if (matches == null) {
      lineFormulaArray = formula.split(/[+-/\\*\\]/);
    } else {
      const formulaLength = formula.length;
      let subFormula = "";
      subFormula = formula.substring(formula.indexOf("("), formulaLength - 1);
      lineFormulaArray = subFormula.split(/[+-,/\\*\\]/);
    }
    
    this.currentOccValChanged = false;
    
    for (let i = 0; i < lineFormulaArray.length; i++) {
      let lineAmt = 0;
      
      if (lineFormulaArray[i].indexOf("(") > -1 && lineFormulaArray[i].indexOf(")") > -1) {
        lineFormulaArray[i] = lineFormulaArray[i].replace(/"/g, "").replace(/'/g, "").replace(/\(|\)/g, "");
        
        const OldAtrribVal = this.f8824OriginalData.filter((obj: any) => 
          obj.ATTRIB_SHORT_CODE === lineFormulaArray[i].trim()
        );
        
        const currentNewAtrribVal = _.filter(this.f8824MasterData, (o: any) => 
          o.ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()
        );

        if (currentNewAtrribVal.length > 0 && currentNewAtrribVal[0].ATTRIB_SHORT_CODE == lineFormulaArray[i].trim()) {
          lineAmt = (currentNewAtrribVal[0].ATTRIB_VALUE);

          if (OldAtrribVal.length > 0 && lineAmt != parseFloat(OldAtrribVal[0].ATTRIB_VALUE)) {
            this.currentOccValChanged = true;
          }
          
          if (parseFloat(lineAmt.toString()) >= 0) {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", lineAmt.toString());
          } else {
            formula = formula.replace("(" + lineFormulaArray[i].trim() + ")", "(" + lineAmt + ")");
          }
        }
      }
    }
  
    try {
      const calculatedVal = safeEvalFormula(formula, null, 'f-8824');
      if (null != calculatedVal && typeof calculatedVal !== 'undefined' && isFiniteEvalNumber(calculatedVal)) {
        return parseFloat(calculatedVal.toFixed(0));
      } else {
        return 0;
      }
    } catch (e) {
      console.error('Error calculating formula:', e);
      return 0;
    }
  }

  formatDate(row: any) {
    // Format date for display
    if (row.ATTRIB_VALUE) {
      const d = new Date(row.ATTRIB_VALUE);
      row["taxyearBE"] = this.formatDateForSave(d);
    }
  }

  /**
   * Convert date from MM/DD/YYYY format (API) to YYYY-MM-DD format (HTML5 date input)
   */
  convertToDateInputFormat(dateStr: string): string {
    if (!dateStr || dateStr === 'VARIOUS' || dateStr === '') {
      return '';
    }
    
    // Check if already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    // Parse MM/DD/YYYY format
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      const month = parts[0].padStart(2, '0');
      const day = parts[1].padStart(2, '0');
      const year = parts[2];
      return `${year}-${month}-${day}`;
    }
    
    return '';
  }

  /**
   * Handle date change from HTML5 date input
   * Converts YYYY-MM-DD to MM/DD/YYYY and stores in row
   */
  onDateChange(dateStr: string, row: any) {
    if (!dateStr) {
      row.ATTRIB_VALUE = '';
      return;
    }
    
    // Convert YYYY-MM-DD to MM/DD/YYYY
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      row.ATTRIB_VALUE = `${month}/${day}/${year}`;
      
      // Call valuechanged to mark as changed
      this.valuechanged(row, row.OCCURRENCE, row.LINE_NO, row.DATA_TYPE);
    }
  }

  openCalendar(row: any) {
    row['isOpen'] = true;
  }

  openCalendar1(row: any) {
    row['isOpen'] = true;
  }

  onCheckboxChange(event: Event, row: any) {
    const target = event.target as HTMLInputElement;
    row.ATTRIB_VALUE = target.checked ? 'true' : 'false';
    this.valuechanged(row, row.OCCURRENCE, row.LINE_NO, row.DATA_TYPE);
    this.recomputeCanSave();
  }

  onRadioCheckboxChange(event: Event, rowData: any) {
    const target = event.target as HTMLInputElement;
    rowData.ATTRIB_VALUE = target.checked ? 'true' : 'false';
    this.checkBoxHandler(rowData.FORM_LINE_KEY, rowData.LINE_ATTRIB_KEY, rowData.ATTRIB_COL, rowData.OCCURRENCE);
    this.valuechanged(rowData, rowData.OCCURRENCE, rowData.LINE_NO, rowData.DATA_TYPE);
    this.recomputeCanSave();
  }

  cancel() {
    if (_.isEqual(this.f8824MasterData, this.f8824OriginalData)) {
      this.requestGridUpdate.emit('cancel');
    } else {
      if (this.crudLoading) {
        this.gtwSnackbarService.openSnackBar("The transaction is still in progress", 'gtw-snackbar--yellow');
      } else {
        this.requestGridUpdate.emit('cancel');
      }
    }
  }
}

